/**
 * File Upload Service
 * Handles file uploads to the server via /api/v2/files endpoints
 */

import MerchantProductApiClient from './merchantProductApiClient'
import type {
  FileType,
  FileUploadResponse,
  FileUploadError
} from '../types/FileUpload'

class FileUploadService {
  private static instance: FileUploadService
  private apiClient: MerchantProductApiClient

  private constructor() {
    this.apiClient = new MerchantProductApiClient()
  }

  static getInstance(): FileUploadService {
    if (!this.instance) {
      this.instance = new FileUploadService()
    }
    return this.instance
  }

  /**
   * Upload a single file to the server
   * @param file - File object to upload
   * @param type - Type of file (image, video, certificate, document, audio)
   * @returns Promise with file upload response containing file_id
   */
  async uploadFile(file: File, type: FileType): Promise<FileUploadResponse> {
    return this.retryUpload(() => this.performUpload(file, type), 3)
  }

  /**
   * Upload multiple files in parallel (max 3 at a time for performance)
   * @param files - Array of File objects to upload
   * @param type - Type of files
   * @returns Promise with array of file upload responses
   */
  async uploadFiles(files: File[], type: FileType): Promise<FileUploadResponse[]> {
    if (files.length === 0) return []

    // Upload max 3 files in parallel for performance
    const CHUNK_SIZE = 3
    const chunks = this.chunkArray(files, CHUNK_SIZE)
    const results: FileUploadResponse[] = []

    for (const chunk of chunks) {
      const chunkResults = await Promise.all(
        chunk.map(file => this.uploadFile(file, type))
      )
      results.push(...chunkResults)
    }

    return results
  }

  /**
   * Upload a file from a URL
   * @param url - URL of the file to download and upload
   * @param type - Type of file
   * @param name - Optional custom filename
   * @returns Promise with file upload response
   */
  async uploadFromUrl(
    url: string,
    type: FileType,
    name?: string
  ): Promise<FileUploadResponse> {
    return this.retryUpload(() => this.performUrlUpload(url, type, name), 3)
  }

  /**
   * Perform the actual file upload
   * @private
   */
  private async performUpload(file: File, type: FileType): Promise<FileUploadResponse> {
    const token = await this.apiClient.getAuthToken()
    if (!token) {
      throw this.createError('UNAUTHORIZED', 'No authentication token available')
    }

    // Create FormData for multipart upload
    const formData = new FormData()
    formData.append('file', file)
    formData.append('type', type)
    formData.append('source_channel', 'product_merchant')

    const response = await fetch(`${(this.apiClient as any).baseUrl}/api/v2/files/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
        // Note: Don't set Content-Type for FormData, browser will set it with boundary
      },
      body: formData
    })

    if (!response.ok) {
      await this.handleErrorResponse(response, 'File upload failed')
    }

    const result = await response.json()

    if (!result.success) {
      throw this.createError(
        result.error?.code || 'UPLOAD_ERROR',
        result.error?.message || 'File upload failed'
      )
    }

    return {
      file_id: result.data.file.id,
      file_url: result.data.file.url,
      file_hash: result.data.file.hash,
      file_name: result.data.file.name,
      file_size: result.data.file.size,
      mime_type: result.data.file.mime_type,
      is_new: result.data.is_new
    }
  }

  /**
   * Perform URL-based file upload
   * @private
   */
  private async performUrlUpload(
    url: string,
    type: FileType,
    name?: string
  ): Promise<FileUploadResponse> {
    const token = await this.apiClient.getAuthToken()
    if (!token) {
      throw this.createError('UNAUTHORIZED', 'No authentication token available')
    }

    const response = await fetch(`${(this.apiClient as any).baseUrl}/api/v2/files/upload-from-url`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        url,
        type,
        source_channel: 'product_merchant',
        ...(name && { name })
      })
    })

    if (!response.ok) {
      await this.handleErrorResponse(response, 'URL upload failed')
    }

    const result = await response.json()

    if (!result.success) {
      throw this.createError(
        result.error?.code || 'UPLOAD_ERROR',
        result.error?.message || 'URL upload failed'
      )
    }

    return {
      file_id: result.data.file.id,
      file_url: result.data.file.url,
      file_hash: result.data.file.hash,
      file_name: result.data.file.name,
      file_size: result.data.file.size,
      mime_type: result.data.file.mime_type,
      is_new: result.data.is_new
    }
  }

  /**
   * Retry upload with exponential backoff
   * @private
   */
  private async retryUpload<T>(
    uploadFn: () => Promise<T>,
    maxRetries: number
  ): Promise<T> {
    let lastError: Error | undefined

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await uploadFn()
      } catch (error) {
        lastError = error as Error
        console.error(`Upload attempt ${attempt} failed:`, error)

        // Don't retry on authentication or validation errors
        if (this.isNonRetryableError(error)) {
          throw error
        }

        // Wait before retrying (exponential backoff)
        if (attempt < maxRetries) {
          await this.sleep(1000 * attempt)
        }
      }
    }

    throw lastError || this.createError('UPLOAD_ERROR', 'Upload failed after maximum retries')
  }

  /**
   * Check if error should not be retried
   * @private
   */
  private isNonRetryableError(error: any): boolean {
    const nonRetryableErrors = ['UNAUTHORIZED', 'VALIDATION_ERROR', 'PAYLOAD_TOO_LARGE']
    return error && nonRetryableErrors.includes(error.code)
  }

  /**
   * Handle error response from API
   * @private
   */
  private async handleErrorResponse(response: Response, defaultMessage: string): Promise<never> {
    let errorMessage = defaultMessage
    let errorCode = 'UPLOAD_ERROR'

    try {
      const errorData = await response.json()
      if (errorData.error) {
        errorMessage = errorData.error.message || defaultMessage
        errorCode = errorData.error.code || errorCode
      }
    } catch {
      // If parsing fails, use default message
      errorMessage = `${defaultMessage} (${response.status} ${response.statusText})`
    }

    throw this.createError(errorCode, errorMessage)
  }

  /**
   * Create a structured error
   * @private
   */
  private createError(code: string, message: string): FileUploadError {
    const error = new Error(message) as Error & FileUploadError
    error.code = code
    error.message = message
    return error
  }

  /**
   * Utility to chunk an array
   * @private
   */
  private chunkArray<T>(array: T[], size: number): T[][] {
    const chunks: T[][] = []
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size))
    }
    return chunks
  }

  /**
   * Utility to sleep
   * @private
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }
}

// Export singleton instance
export default FileUploadService.getInstance()
